import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Star, MapPin, Award, TrendingUp, Stethoscope, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import { getDoctors, getMe, getRecommendations } from '../services/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredDoctors, setFeaturedDoctors] = useState([]);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    };
    checkAuth();

    const loadFeatured = async () => {
      try {
        const response = await getDoctors({ featured: true });
        setFeaturedDoctors(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error loading featured doctors:', error);
      }
    };
    loadFeatured();

    const loadRecommendations = async () => {
      try {
        const response = await getRecommendations();
        if (response.data.doctors?.length > 0) {
          setRecommendations(response.data);
        }
      } catch (error) {
        console.error('Error loading recommendations:', error);
      }
    };
    loadRecommendations();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/directory');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      {/* Hero Section */}
      <section className="relative py-20 px-6 md:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Content - 6 cols */}
            <div className="lg:col-span-6 space-y-6">
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0F2A44] tracking-tight leading-tight">
                Find Trusted Healthcare Providers Near You
              </h1>
              <p className="font-public text-lg text-[#4B5563] leading-relaxed">
                Discover qualified doctors, read verified reviews, and book appointments with confidence. Your health journey starts here.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex gap-3" data-testid="hero-search-form">
                <Input
                  type="text"
                  placeholder="Search by doctor name, specialty, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="hero-search-input"
                  className="h-14 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-[#4A90E2]/20 font-public"
                />
                <Button
                  type="submit"
                  data-testid="hero-search-btn"
                  className="h-14 px-8 bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white rounded-lg font-manrope font-semibold"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </form>

              {/* Quick Stats */}
              <div className="flex gap-8 pt-4">
                <div>
                  <div className="font-space font-medium text-2xl text-[#2DD4BF]">500+</div>
                  <div className="text-sm text-[#4B5563] font-public">Verified Doctors</div>
                </div>
                <div>
                  <div className="font-space font-medium text-2xl text-[#2DD4BF]">2,000+</div>
                  <div className="text-sm text-[#4B5563] font-public">Patient Reviews</div>
                </div>
                <div>
                  <div className="font-space font-medium text-2xl text-[#2DD4BF]">50+</div>
                  <div className="text-sm text-[#4B5563] font-public">Specialties</div>
                </div>
              </div>

              {/* Doctor Enlist CTA - Always visible */}
              <div className="pt-4">
                <Link to="/doctor/register">
                  <Button
                    data-testid="hero-enlist-btn"
                    className="w-full sm:w-auto bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] rounded-lg font-manrope font-semibold h-12 px-6"
                  >
                    <Stethoscope className="h-5 w-5 mr-2" />
                    Are you a Doctor? Enlist Here
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image - 6 cols */}
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1678695972687-033fa0bdbac9?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Black African Kenyan healthcare professional"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-manrope font-bold text-3xl text-[#0F2A44] text-center mb-12">
            Why Choose Afyagram?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-lg flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-[#2DD4BF]" />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-2">
                Verified Reviews
              </h3>
              <p className="font-public text-[#4B5563] leading-relaxed">
                Read authentic patient experiences to make informed decisions about your healthcare.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-[#4A90E2]" />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-2">
                Location-Based Search
              </h3>
              <p className="font-public text-[#4B5563] leading-relaxed">
                Find doctors near you with detailed location information and directions.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#0F2A44]/10 rounded-lg flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-[#0F2A44]" />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-2">
                Credential Verification
              </h3>
              <p className="font-public text-[#4B5563] leading-relaxed">
                Access detailed information about doctors' education, certifications, and experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Match - Personalized Recommendations */}
      {recommendations && recommendations.doctors.length > 0 && (
        <section className="py-16 px-6 md:px-12 bg-gradient-to-br from-[#0F2A44] to-[#1a3d5c]" data-testid="smart-match-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="h-6 w-6 text-[#2DD4BF]" />
              <h2 className="font-manrope font-bold text-2xl md:text-3xl text-white">
                {recommendations.reason === 'personalized'
                  ? 'Smart Match - Recommended for You'
                  : 'Top Rated Doctors'}
              </h2>
            </div>
            {recommendations.matched_specialty && (
              <p className="text-[#2DD4BF]/80 font-public text-sm mb-8 ml-9">
                Based on your interest in {recommendations.matched_specialty}
              </p>
            )}
            {recommendations.reason === 'top_rated' && (
              <p className="text-slate-300 font-public text-sm mb-8 ml-9">
                Our highest-rated healthcare professionals
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recommendations.doctors.slice(0, 3).map((doctor) => (
                <div
                  key={doctor.doctor_id}
                  onClick={() => navigate(`/doctor/${doctor.doctor_id}`)}
                  className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/10 p-6 cursor-pointer hover:bg-white/15 transition-all"
                  data-testid={`smart-match-${doctor.doctor_id}`}
                >
                  {doctor.photo_url ? (
                    <img
                      src={doctor.photo_url}
                      alt={doctor.name}
                      className="w-16 h-16 rounded-full object-cover mx-auto mb-4 border-2 border-[#2DD4BF]/30"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-xl text-white font-manrope font-bold">
                        {doctor.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <h3 className="font-manrope font-semibold text-white text-center mb-1">
                    {doctor.title} {doctor.name}
                  </h3>
                  <p className="text-[#2DD4BF] text-sm text-center mb-3">
                    {doctor.specialty}
                  </p>
                  <div className="flex items-center justify-center gap-1 text-sm mb-2">
                    <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                    <span className="font-space font-medium text-white">
                      {doctor.average_rating?.toFixed(1) || '0.0'}
                    </span>
                    <span className="text-white/60 text-xs">
                      ({doctor.total_reviews || 0})
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs text-white/50">
                    <MapPin className="h-3 w-3" />
                    <span>{doctor.region}</span>
                  </div>
                </div>
              ))}
            </div>

            {recommendations.doctors.length > 3 && (
              <div className="text-center mt-8">
                <Button
                  onClick={() => navigate(recommendations.matched_specialty ? `/directory?specialty=${encodeURIComponent(recommendations.matched_specialty)}` : '/all-doctors')}
                  className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-semibold rounded-full px-8"
                  data-testid="smart-match-view-all"
                >
                  View All Recommendations
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Doctors */}
      {featuredDoctors.length > 0 && (
        <section className="py-16 px-6 md:px-12 bg-[#FAFAF9]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-manrope font-bold text-3xl text-[#0F2A44]">
                Featured Doctors
              </h2>
              <Button
                onClick={() => navigate('/directory?featured=true')}
                variant="ghost"
                className="text-[#4A90E2] hover:text-[#4A90E2]/80"
              >
                View All →
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredDoctors.map((doctor) => (
                <div
                  key={doctor.doctor_id}
                  onClick={() => navigate(`/doctor/${doctor.doctor_id}`)}
                  className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover-lift"
                  data-testid={`featured-doctor-${doctor.doctor_id}`}
                >
                  {doctor.photo_url ? (
                    <img
                      src={doctor.photo_url}
                      alt={doctor.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl text-slate-500 font-manrope font-bold">
                        {doctor.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <h3 className="font-manrope font-semibold text-lg text-[#0F2A44] text-center mb-1">
                    {doctor.title} {doctor.name}
                  </h3>
                  <p className="font-public text-sm text-[#4B5563] text-center mb-3">
                    {doctor.specialty}
                  </p>
                  <div className="flex items-center justify-center gap-1 text-sm mb-3">
                    <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                    <span className="font-space font-medium text-[#262626]">
                      {doctor.average_rating.toFixed(1)}
                    </span>
                    <span className="text-[#4B5563] font-public text-xs">
                      ({doctor.total_reviews} reviews)
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs border-t border-slate-100 pt-3">
                    <MapPin className="h-3 w-3 text-[#4B5563]" />
                    <span className="text-[#4B5563] font-public">{doctor.region}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section with bottom search */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-manrope font-bold text-4xl text-[#0F2A44] mb-6">
            Ready to Find Your Doctor?
          </h2>
          <p className="font-public text-lg text-[#4B5563] mb-8 leading-relaxed">
            Start your search now and connect with the right healthcare professional for your needs.
          </p>

          <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl mx-auto" data-testid="cta-search-form">
            <Input
              type="text"
              placeholder="Search doctors, specialties, or locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="cta-search-input"
              className="h-14 rounded-lg border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#4A90E2]/20 font-public"
            />
            <Button
              type="submit"
              data-testid="cta-search-btn"
              className="h-14 px-8 bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] rounded-lg font-manrope font-semibold"
            >
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 bg-[#0F2A44] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src="/afyagram_logo.png" alt="Afyagram" className="h-12 w-auto brightness-0 invert" />
          </div>
          <p className="font-public text-sm text-slate-300 mb-4">
            Connecting patients with trusted healthcare providers
          </p>
          <p className="font-public text-xs text-slate-400">
            © 2025 Afyagram. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}